import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingCart, Menu, X, Gift, BookOpen, HelpCircle, Star, Clock, Award, Shield, ChevronLeft, ChevronRight, LogOut, Bell } from 'lucide-react';

import useSeo from './utils/useSeo';
// ✅ Import های عادی (برای صفحات مهم)
import logoNoor from './logo_noor.png';
import { CartProvider, useCart } from './contexts/CartContext';
import { ToastContainer, showToast } from './components/Toast';
import { ShoppingCartSidebar } from './components/ShoppingCart';
import { SearchModal } from './components/SearchModel';
import LiveGoldPrice from './components/LiveGoldPrice';
import { GoldPriceCalculator } from './components/GoldPriceCalculator';
import { ProductCard } from './components/ProductCard';
import { useWishlist } from './contexts/WishlistContext';
import { BrowserRouter } from 'react-router-dom';
import { WishlistProvider } from './contexts/WishlistContext';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationBell from './components/NotificationBell';
import NotificationListener from './components/NotificationListener';
import { LiveChat } from './components/LiveChat';
import SiteFooter from './components/SiteFooter';

// ✅ Lazy Loading برای صفحات بزرگ (بهینه‌سازی)
const AboutUsPage = lazy(() => import('./components/AboutUs'));
const ProductListingPage = lazy(() => import('./components/ProductListing'));
const ProductDetailPage = lazy(() => import('./components/ProductDetail'));
const Profile = lazy(() => import('./components/Profile'));
const AdvancedGoldBank = lazy(() => import('./components/AdvanceGold'));
const AuthPage = lazy(() => import('./components/Auth'));
const ForgotPasswordPage = lazy(() => import('./components/ForgotPassword'));
const CheckoutPage = lazy(() => import('./components/Checkout'));
const WishlistPage = lazy(() => import('./components/Wishlist'));
const SpecialOffersPage = lazy(() => import('./components/SpecialOffers'));
const ConsultationPage = lazy(() => import('./components/Consultation'));
const ProductComparisonPage = lazy(() => import('./components/ProductComparison'));
const TermsPage = lazy(() => import('./components/Terms'));

// ✅ کامپوننت Loading
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-500 mx-auto mb-4"></div>
      <p className="text-gray-600 text-lg">در حال بارگذاری...</p>
    </div>
  </div>
);

// ✅ صفحه 404
const NotFoundPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
      <div className="text-center px-4">
        <div className="mb-8">
          <h1 className="text-9xl font-bold text-amber-600 mb-4">404</h1>
          <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-yellow-500 mx-auto mb-6"></div>
        </div>
        
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          صفحه مورد نظر یافت نشد
        </h2>
        <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
          متأسفانه صفحه‌ای که به دنبال آن هستید وجود ندارد یا حذف شده است.
        </p>
        
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow"
          >
            بازگشت به خانه
          </button>
          <button
            onClick={() => navigate(-1)}
            className="border-2 border-amber-500 text-amber-600 px-8 py-3 rounded-lg font-semibold hover:bg-amber-50 transition-colors"
          >
            بازگشت به صفحه قبل
          </button>
        </div>
        
        <div className="mt-12">
          <p className="text-gray-500 mb-4">لینک‌های مفید:</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/products" className="text-amber-600 hover:text-amber-700 underline">محصولات</Link>
            <Link to="/gold-bank" className="text-amber-600 hover:text-amber-700 underline">بانک طلا</Link>
            <Link to="/about-us" className="text-amber-600 hover:text-amber-700 underline">درباره ما</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ Header پیشرفته
const AdvancedHeader = ({ onCartOpen, onSearchOpen }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const { cartCount } = useCart();
  const { clearWishlist } = useWishlist();

  useEffect(() => {
    // ✅ باید یک‌بار موقع بالا آمدن اپ صدا زده بشه تا کوکی csrftoken ست بشه —
    // بدون این، هیچ POST/PUT/DELETE ای کار نمی‌کنه (403 می‌گیره)
    import('./services/api').then(({ apiService }) => apiService.primeCsrf());

    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing user:', error);
      }
    }
  }, []);

  const handleLogout = async () => {
    if (window.confirm('آیا می‌خواهید از حساب کاربری خود خارج شوید؟')) {
      const { apiService } = await import('./services/api');
      await apiService.logout();
      localStorage.removeItem('cart'); 
      clearWishlist();
      setUser(null);
      window.dispatchEvent(new Event('userChanged'));
      navigate('/');
      window.location.reload();
    }
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src={logoNoor} alt="لوگوی جواهری نور" className="w-12 h-12 ml-4 rounded-full object-cover" />
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-stone-800 to-stone-800">
              جواهری نور
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12">
            <Link to="/" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">خانه</Link><hr />
            <Link to="/products" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">محصولات</Link>
            <Link to="/gold-bank" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">بانک طلا</Link>
            <a href="#education" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">آموزش</a>
            <Link to="/about-us" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">درباره ما</Link>
            <Link to="/terms" className="hidden lg:block text-xl text-gray-700 hover:text-amber-600 transition-colors">قوانین</Link>
            <Link to="/compare" className="text-xl text-gray-700 hover:text-amber-600 transition-colors">مقایسه</Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* ✅ Notifications — یک کامپوننت واحد با Context */}
            {user && <NotificationBell />}
            
            {/* Search */}
            <button onClick={onSearchOpen} className="p-2 mx-4 hover:bg-amber-50 rounded-full transition-colors">
              <Search className="w-6 h-6 text-gray-700" />
            </button>
            
            {/* User Menu */}
            {user ? (
              <div className="relative group">
                <button className="p-2 hover:bg-amber-50 rounded-full transition-colors flex items-center gap-2">
                  <User className="w-6 h-6 text-gray-700" />
                  <span className="hidden md:block text-sm font-medium text-gray-700">
                    {user.first_name || user.phone}
                  </span>
                </button>
                
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                  <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50">
                    پروفایل من
                  </Link>
                  <Link to="/profile?tab=orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50">
                    سفارشات
                  </Link>
                  <Link to="/wishlist" className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50">
                    علاقه‌مندی‌ها
                  </Link>
                  <hr className="my-2" />
                  <button 
                    onClick={handleLogout}
                    className="w-full text-right px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    خروج
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/auth" className="px-4 py-2 bg-amber-200/70 text-black rounded-full hover:bg-amber-400 transition-colors">
                ورود / ثبت‌نام
              </Link>
            )}
            
            {/* Cart */}
            {/* Cart - فقط برای کاربران لاگین شده */}
            {user && (
              <button onClick={onCartOpen} className="p-2 hover:bg-amber-50 rounded-full transition-colors relative">
                <ShoppingCart className="w-6 h-6 text-gray-700" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            
            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="md:hidden p-2 hover:bg-amber-50 rounded-full"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Mobile Menu - کامل شده */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="p-4 space-y-2">
            <Link 
              to="/" 
              className="block py-2 text-gray-700 hover:text-amber-600" 
              onClick={() => setIsMenuOpen(false)}
            >
              خانه
            </Link>
            <Link 
              to="/products" 
              className="block py-2 text-gray-700 hover:text-amber-600" 
              onClick={() => setIsMenuOpen(false)}
            >
              محصولات
            </Link>
            <Link 
              to="/gold-bank" 
              className="block py-2 text-gray-700 hover:text-amber-600" 
              onClick={() => setIsMenuOpen(false)}
            >
              بانک طلا
            </Link>
            <Link 
              to="/about-us" 
              className="block py-2 text-gray-700 hover:text-amber-600" 
              onClick={() => setIsMenuOpen(false)}
            >
              درباره ما
            </Link>
            <Link 
              to="/terms" 
              className="block py-2 text-gray-700 hover:text-amber-600" 
              onClick={() => setIsMenuOpen(false)}
            >
              قوانین و مقررات
            </Link>
            
            {user && (
              <>
                <hr className="my-2" />
                <Link 
                  to="/profile" 
                  className="block py-2 text-gray-700 hover:text-amber-600" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  پروفایل
                </Link>
                <Link 
                  to="/wishlist" 
                  className="block py-2 text-gray-700 hover:text-amber-600" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  علاقه‌مندی‌ها
                </Link>
                <button 
                  onClick={() => { 
                    handleLogout(); 
                    setIsMenuOpen(false); 
                  }} 
                  className="block w-full text-right py-2 text-red-600"
                >
                  خروج
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

// ✅ صفحه اصلی (HomePage) - محصولات از API
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const normalizeProduct = (p) => {
  const base = Number(p.original_price || p.current_price || 0);
  const discounted = p.discounted_price ? Number(p.discounted_price) : null;
  const imgUrl = p.main_image
    ? (p.main_image.startsWith('http') ? p.main_image : `${(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api$/, '')}${p.main_image}`)
    : '';
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    category: p.category?.name || '',
    categorySlug: p.category?.slug || '',
    weight: Number(p.weight || 0),
    karat: String(p.karat || 18),
    price: discounted ?? base,
    originalPrice: base,
    discount: Number(p.discount_percent || 0),
    discount_percent: Number(p.discount_percent || 0),
    discounted_price: discounted,
    stock: Number(p.stock || 0),
    inStock: p.is_available && Number(p.stock) > 0,
    image: imgUrl,
    images: [],
    features: [`طلای ${p.karat} عیار`, `وزن: ${p.weight} گرم`],
    rating: 0,
    reviewCount: 0,
  };
};

const HomePage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [apiBanners, setApiBanners] = useState([]); // بنرهای آپلودشده از پنل ادمین

  // ── محصولات از API ───────────────────────────────────────────────
  const [allProducts, setAllProducts]   = useState([]);
  const [categories, setCategories]     = useState([
    { id: 'all', name: 'همه محصولات', icon: '💎' }
  ]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    // واکشی محصولات
    fetch(`${API_BASE}/products/`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results ?? [];
        setAllProducts(list.map(normalizeProduct));
      })
      .catch(() => {})
      .finally(() => setProductsLoading(false));

    // واکشی دسته‌بندی‌ها
    fetch(`${API_BASE}/categories/`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results ?? [];
        setCategories([
          { id: 'all', name: 'همه محصولات', icon: '💎' },
          ...list.filter(c => c.is_active !== false).map(c => ({
            id: c.slug,
            name: c.name,
            icon: c.icon || '🔶',
          }))
        ]);
      })
      .catch(() => {});

    // واکشی بنرهای آپلودشده از پنل ادمین
    fetch(`${API_BASE}/banners/`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results ?? [];
        setApiBanners(list);
      })
      .catch(() => {});
  }, []);

  // فیلتر بر اساس دسته‌بندی انتخاب‌شده
  const filteredProducts = activeCategory === 'all'
    ? allProducts
    : allProducts.filter(p => p.categorySlug === activeCategory || p.category === activeCategory);

  // طلاهای سبک = دسته‌بندی light
  const lightProducts = allProducts.filter(
    p => p.categorySlug === 'light' || p.category === 'طلاهای سبک'
  );

  const faqs = [
    { q: 'نحوه محاسبه قیمت طلای نو چگونه است؟', a: 'قیمت طلای نو بر اساس وزن، عیار و قیمت روز طلا به اضافه اجرت ساخت محاسبه می‌شود.' },
    { q: 'آیا امکان خرید اقساطی وجود دارد؟', a: 'بله، شما می‌توانید از طریق بانک طلا به صورت هفتگی یا ماهانه پس‌انداز کنید.' },
    { q: 'ضمانت محصولات چگونه است؟', a: 'تمامی محصولات دارای ضمانت اصالت و گارانتی یک ساله هستند.' }
  ];

  const banners = [
    {
      id: 1,
      title: 'تخفیف ویژه عید',
      subtitle: 'تا 5% تخفیف روی سرویس‌های طلا',
      link: '/special-offers',
      buttonText: 'مشاهده تخفیف‌ها',
      media: '/Gemini_Generated_Image_to51zato51zato51.png',
      media_type: 'image',
      text_position: 'left',     // فضای خالی این عکس سمت چپ است
      text_color: 'dark',
    },
    {
      id: 2,
      title: 'بانک طلای آنلاین',
      subtitle: 'پس‌انداز طلا با قیمت روز',
      link: '/gold-bank',
      buttonText: 'ورود به بانک طلا',
      media: '/Gemini_Generated_Image_ia7gg3ia7gg3ia7g.png',
      media_type: 'image',
      text_position: 'right',    // صندوق سمت چپ، متن سمت راست
      text_color: 'dark',
    },
    {
      id: 3,
      title: 'مشاوره رایگان',
      subtitle: 'راهنمایی در انتخاب بهترین طلا',
      link: '/consultation',
      buttonText: 'دریافت مشاوره',
      media: '/Gemini_Generated_Image_6wa1go6wa1go6wa1.png',
      media_type: 'image',
      text_position: 'left',     // بوکه‌ی محو سمت چپ برای متن
      text_color: 'dark',
    },
  ];


  const handleAddToCart = (productId) => {
    // ✅ چک کردن لاگین کاربر
    const user = localStorage.getItem('user');
    if (!user) {
      // نمایش تأییدیه و هدایت به صفحه ورود
      if (window.confirm('برای افزودن محصول به سبد خرید ابتدا وارد شوید\n\nآیا می‌خواهید به صفحه ورود بروید؟')) {
        navigate('/auth');
      }
      return;
    }

    // اگر لاگین کرده باشد، محصول رو اضافه کن
    const result = addToCart(productId);
    if (result && result.success) {
      showToast('محصول به سبد خرید اضافه شد!');
    } else if (result && result.message) {
      showToast(result.message, 'error');
    }
  };

  // اگر از پنل ادمین بنر آپلود شده باشد، همان‌ها نمایش داده می‌شوند؛
  // در غیر این صورت بنرهای پیش‌فرض بالا نمایش داده می‌شوند.
  const slides = apiBanners.length > 0 ? apiBanners : banners;

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // چرخش خودکار اسلایدها هر ۵ ثانیه
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(
      () => setCurrentSlide((prev) => (prev + 1) % slides.length),
      5000
    );
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <AdvancedHeader onCartOpen={() => setIsCartOpen(true)} onSearchOpen={() => setIsSearchOpen(true)} />

      {/* Hero Slider — پشتیبانی از عکس/GIF/ویدئو آپلودشده از پنل ادمین */}
      <section className="relative h-96 overflow-hidden">
        <div className="relative h-full">
          {slides.map((banner, index) => {
            // پشتیبانی هم‌زمان از بنرهای API و بنرهای پیش‌فرض
            const link       = banner.button_link || banner.link || '/products';
            const buttonText = banner.button_text || banner.buttonText;
            const pos        = banner.text_position || 'center';
            // توجه: چون سایت راست‌چین (RTL) است، برای «چپِ دیداری» باید items-end
            // و برای «راستِ دیداری» باید items-start استفاده شود.
            const textAlign  = pos === 'right' ? 'text-right items-start'
                              : pos === 'left' ? 'text-left items-end'
                              : 'text-center items-center';
            // رنگ متن: اگر در ادمین تعیین شده از همان، وگرنه روی عکس سفید و روی گرادیان تیره
            const darkText   = banner.text_color ? banner.text_color === 'dark' : !banner.media;
            const textColorClass = darkText ? 'text-stone-800' : 'text-white';
            const textShadow = darkText
              ? '0 1px 8px rgba(255,255,255,0.6)'   // هاله برای متن تیره روی زمینه روشن
              : '0 2px 8px rgba(0,0,0,0.55)';       // سایه برای متن سفید
            return (
              <div
                key={banner.id ?? index}
                className={`absolute inset-0 transition-transform duration-500 ${
                  index === currentSlide ? 'translate-x-0' : index < currentSlide ? '-translate-x-full' : 'translate-x-full'
                }`}
              >
                {/* رسانه‌ی پس‌زمینه */}
                {banner.media ? (
                  banner.media_type === 'video' ? (
                    <video
                      src={banner.media}
                      autoPlay loop muted playsInline
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={banner.media}
                      alt={banner.title || 'بنر'}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-300 via-amber-300/50 to-amber-300" />
                )}

                {/* لایه‌ی تیره فقط وقتی متن سفید است (برای خوانایی روی عکس) */}
                {!darkText && banner.media && (banner.title || banner.subtitle || buttonText) && (
                  <div className="absolute inset-0 bg-black/20" />
                )}

                {/* متن روی بنر — جای متن و رنگ متن قابل تنظیم از پنل ادمین */}
                {(banner.title || banner.subtitle || buttonText) && (
                  <div className={`absolute inset-0 flex flex-col justify-center ${textAlign} px-12 md:px-20`}>
                    <div className={`${textColorClass} ${pos !== 'center' ? 'md:max-w-[48%]' : ''}`}>
                      {banner.title && (
                        <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4" style={{ textShadow }}>
                          {banner.title}
                        </h2>
                      )}
                      {banner.subtitle && (
                        <p className="text-base md:text-xl" style={{ textShadow }}>{banner.subtitle}</p>
                      )}
                      {buttonText && (
                        <button
                          onClick={() => navigate(link)}
                          className="mt-5 md:mt-6 bg-amber-500 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl hover:bg-amber-600 transition-all"
                        >
                          {buttonText}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white z-20">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white z-20">
          <ChevronRight className="w-6 h-6" />
        </button>
        {/* نقطه‌های نشانگر */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${index === currentSlide ? 'bg-amber-500' : 'bg-white/70'}`}
            />
          ))}
        </div>
      </section>

      <LiveGoldPrice />

      {/* Categories */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">دسته‌بندی محصولات</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((cat) => (
              <button 
                key={cat.id} 
                onClick={() => setActiveCategory(cat.id)} 
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  activeCategory === cat.id 
                    ? 'bg-amber-200 text-stone-800 shadow-md' 
                    : 'bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">محصولات</h2>
            <Link to="/products" className="text-amber-600 hover:text-amber-700 font-semibold text-sm flex items-center gap-1">
              مشاهده همه ←
            </Link>
          </div>
          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-lg p-4 animate-pulse">
                  <div className="bg-amber-100 h-48 rounded-lg mb-4" />
                  <div className="bg-gray-200 h-4 rounded w-3/4 mb-2" />
                  <div className="bg-gray-200 h-4 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-5xl mb-4">🔍</p>
              <p className="text-lg">محصولی در این دسته‌بندی وجود ندارد</p>
              
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProducts.slice(0, 8).map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gold Calculator */}
      <section className="py-12 bg-gradient-to-r from-transparent via-amber-200/90 to-transparent">
        <div className="container mx-auto px-4">
          <GoldPriceCalculator />
        </div>
      </section>

      {/* Light Products */}
      <section className="py-12 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <Gift className="w-8 h-8 text-amber-600 mr-2" />
              <h2 className="text-3xl font-bold">طلاهای سبک (هدیه)</h2>
            </div>
            <Link to="/products?category=light" className="text-amber-600 hover:text-amber-700 font-semibold text-sm">
              مشاهده همه ←
            </Link>
          </div>
          {lightProducts.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              محصولی در دسته «طلاهای سبک» اضافه نشده
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {lightProducts.slice(0, 3).map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Education */}
      <section className="py-12" id="education">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-8">
            <BookOpen className="w-8 h-8 text-amber-600 mr-2" />
            <h2 className="text-3xl font-bold">آموزش خرید و فروش طلا</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 text-amber-700">محاسبه قیمت طلای نو</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  قیمت = (وزن × قیمت روز) + اجرت ساخت
                </li>
                <li className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  اجرت ساخت معمولاً 7 تا 20 درصد است
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 text-orange-700">محاسبه قیمت طلای دست دوم</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  قیمت = وزن × قیمت روز × 0.95
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  5 درصد کسر می‌شود برای سود طلافروشی
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-8">
            <HelpCircle className="w-8 h-8 text-amber-600 mr-2" />
            <h2 className="text-3xl font-bold">سوالات متداول</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-lg mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-gradient-to-r from-amber-100 to-yellow-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'ضمانت اصالت', text: 'تمامی محصولات دارای گواهی اصالت' },
              { icon: Shield, title: 'خرید امن', text: 'پرداخت امن با درگاه بانکی' },
              { icon: Clock, title: 'تحویل سریع', text: 'ارسال در کمترین زمان ممکن' },
              { icon: Star, title: 'کیفیت تضمینی', text: 'بهترین کیفیت ساخت و طراحی' }
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <feature.icon className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      <ShoppingCartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </div>
  );
};

// ✅ صفحه تایید پرداخت
const PaymentVerifyPage = () => {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [refId, setRefId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const trackId = params.get('trackId');
    const successParam = params.get('success');
    const orderId = params.get('order_id');

    if (successParam !== '1') {
      setStatus('failed');
      setMessage('پرداخت توسط شما لغو شد یا ناموفق بود.');
      return;
    }

    const verifyPayment = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const res = await fetch(`${API_BASE_URL}/payment/verify/?trackId=${trackId}&success=${successParam}&order_id=${orderId}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          setStatus('success');
          setRefId(data.ref_id);
          setMessage('پرداخت شما با موفقیت انجام شد.');
        } else {
          setStatus('failed');
          setMessage(data.message || 'پرداخت تایید نشد.');
        }
      } catch {
        setStatus('failed');
        setMessage('خطا در بررسی وضعیت پرداخت.');
      }
    };
    verifyPayment();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">در حال بررسی پرداخت...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✅</span>
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">پرداخت موفق</h2>
            <p className="text-gray-600 mb-2">{message}</p>
            {refId && <p className="text-sm text-gray-500 mb-6">کد پیگیری: <strong>{refId}</strong></p>}
            <button onClick={() => navigate('/profile?tab=orders')} className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors">
              مشاهده سفارشات
            </button>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">پرداخت ناموفق</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button onClick={() => navigate('/checkout')} className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors">
              بازگشت به سبد خرید
            </button>
          </>
        )}
      </div>
    </div>
  );
};


// ✅ App Component با همه Route‌ها
const App = () => {
  // ✅ باید یک‌بار در همون لحظه‌ای که کل اپ بالا میاد صدا زده بشه — نه فقط
  // در صفحه‌ی اصلی — چون کاربر ممکنه مستقیم روی /checkout یا /product/:id
  // یا هر صفحه‌ی دیگه‌ای لود کنه. بدون این کوکی، هر POST/PUT/DELETE با 403 رد می‌شه.
  useEffect(() => {
    import('./services/api').then(({ apiService }) => apiService.primeCsrf());
  }, []);

  return (
    <CartProvider>
      <WishlistProvider>
        <NotificationProvider>
        <NotificationListener />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* ✅ صفحه اصلی */}
            <Route path="/" element={<HomePage />} />
            
            {/* ✅ صفحات محصولات */}
            <Route path="/products" element={<ProductListingPage />} />
            <Route 
              path="/product/:id" 
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ProductDetailPage />
                </Suspense>
              } 
            />
            
            {/* ✅ صفحه درباره ما */}
            <Route path="/about-us" element={<AboutUsPage />} />
            
            {/* ✅ صفحات کاربر */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/wishlist" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WishlistPage />
              </Suspense>
            } />

            <Route path="/special-offers" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SpecialOffersPage />
              </Suspense>
            } />
            
            <Route path="/consultation" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ConsultationPage />
              </Suspense>
            } />

            
            
            {/* ✅ بانک طلا */}
            <Route path="/gold-bank" element={<AdvancedGoldBank />} />
            
            {/* ✅ فرآیند خرید */}
            <Route path="/checkout" element={<CheckoutPage />} />
            
            {/* ✅ احراز هویت */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={   
              <Suspense fallback={<LoadingSpinner />}>
                <ForgotPasswordPage />
              </Suspense>
            } />
            
            {/* مقایسه محصولات */}
            <Route path="/compare" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductComparisonPage />
              </Suspense>
            } />

            {/* ✅ صفحه تایید پرداخت */}
            <Route path="/payment/verify" element={<PaymentVerifyPage />} />

            {/* ✅ قوانین و مقررات */}
            <Route path="/terms" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TermsPage />
              </Suspense>
            } />

            {/* ✅ صفحه 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <SiteFooter />
        </Suspense>
        </NotificationProvider>
      </WishlistProvider>
    <ToastContainer />
    <LiveChat />
    </CartProvider>
  );
};

export default App;


